/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.patients

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material3.Card
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import studio.oddpods.cauce.core.base.designsystem.component.KptTopAppBar
import studio.oddpods.cauce.core.model.patient.Patient
import studio.oddpods.cauce.core.ui.asString
import studio.oddpods.cauce.core.ui.scaffold.KptScaffold
import studio.oddpods.cauce.feature.patients.generated.resources.Res
import studio.oddpods.cauce.feature.patients.generated.resources.patients_add
import studio.oddpods.cauce.feature.patients.generated.resources.patients_empty_subtitle
import studio.oddpods.cauce.feature.patients.generated.resources.patients_empty_title
import studio.oddpods.cauce.feature.patients.generated.resources.patients_list_title

@Composable
fun PatientListScreen(
    onPatientClick: (String) -> Unit,
    modifier: Modifier = Modifier,
    onAddPatientClick: () -> Unit = {},
    viewModel: PatientListViewModel = koinViewModel(),
) {
    val state by viewModel.stateFlow.collectAsStateWithLifecycle()

    KptScaffold(
        modifier = modifier,
        topBar = {
            KptTopAppBar(title = stringResource(Res.string.patients_list_title))
        },
        floatingActionButton = {
            FloatingActionButton(onClick = onAddPatientClick) {
                Icon(Icons.Default.Add, contentDescription = stringResource(Res.string.patients_add))
            }
        },
    ) {
        when {
            state.isLoading -> {
                Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator()
                }
            }
            state.error != null -> {
                Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    Text(state.error!!.asString(), color = MaterialTheme.colorScheme.error)
                }
            }
            state.patients.isEmpty() -> {
                Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                        Text(
                            stringResource(Res.string.patients_empty_title),
                            style = MaterialTheme.typography.titleMedium,
                        )
                        Text(
                            stringResource(Res.string.patients_empty_subtitle),
                            style = MaterialTheme.typography.bodyMedium,
                        )
                    }
                }
            }
            else -> {
                LazyColumn(
                    modifier = Modifier.fillMaxSize(),
                    contentPadding = androidx.compose.foundation.layout.PaddingValues(16.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    items(state.patientsDisplay, key = { it.patient.id }) { item ->
                        PatientCard(
                            patient = item.patient,
                            defaultFeeDisplay = item.defaultFeeDisplay,
                            onClick = { onPatientClick(item.patient.id) },
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun PatientCard(
    patient: Patient,
    defaultFeeDisplay: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Card(
        modifier = modifier.fillMaxWidth().clickable(onClick = onClick),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(16.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Column {
                Text(patient.name, style = MaterialTheme.typography.titleMedium)
                Text(patient.phone, style = MaterialTheme.typography.bodySmall)
            }
            Text(
                defaultFeeDisplay,
                style = MaterialTheme.typography.titleSmall,
            )
        }
    }
}
